import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { fbTrack } from '../lib/consent';
import { track } from '../lib/track';
import { useAuth } from '../contexts/AuthContext';
import { HeroSection, SoftwareSection, ExampleSection, PromisesSection, SimulatorSection, PricingIntro, EInvoicingSection, FaqSection, FinalCta } from '../components/home/HomeSections';
import { ArrowRight, CheckCircle, Shield, Star, Zap, Mail, LayoutDashboard } from 'lucide-react';

const PlanItems = ({ items, color, bold }) => (
  <ul className="space-y-2 text-sm mb-6">
    {items.map(i => (
      <li key={i} className="flex items-start gap-2">
        <CheckCircle className={`h-4 w-4 ${color} flex-shrink-0 mt-0.5`} />
        <span className={bold ? 'font-medium' : ''}>{i}</span>
      </li>
    ))}
  </ul>
);

const PricingSection = () => (
  <section id="tarifs" className="px-4 sm:px-6 py-12 sm:py-20 scroll-mt-16" data-testid="pricing-section">
    <PricingIntro />
    <div className="max-w-5xl mx-auto">
      <p className="text-center text-sm text-slate-600 mb-6">Deux formules, mêmes fonctionnalités — accès immédiat après paiement.</p>
      <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
        <Card className="border-2 border-blue-200 hover:border-blue-400 transition-all" data-testid="pricing-subscription">
          <CardContent className="p-6 sm:p-7">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Zap className="h-5 w-5 text-blue-600" /></div>
              <span className="text-xs font-semibold text-blue-700 uppercase">Abonnement</span>
            </div>
            <p className="font-bold text-lg mb-1">Abonnement mensuel</p>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl sm:text-5xl font-bold">29,99€</span><span className="text-slate-500">/mois</span>
            </div>
            <p className="text-xs text-slate-500 mb-5">Sans engagement · résiliable à tout moment, avec prise d&apos;effet à l&apos;issue de la période mensuelle en cours</p>
            <PlanItems color="text-blue-600" items={['Accès complet au tableau de bord', 'Tous les simulateurs', 'Patrithèque (9 guides inclus)', 'Mises à jour des taux incluses', 'Support par email']} />
            <Link to="/achat?plan=monthly" className="block" data-testid="pricing-cta-subscription"
              onClick={() => { track('mon_ae_monthly_click', { plan: 'monthly', location: 'home' }); fbTrack('InitiateCheckout', { content_name: 'Abonnement Mensuel', value: 29.99, currency: 'EUR' }); }}>
              <Button size="lg" className="w-full h-12 bg-blue-600 hover:bg-blue-700">Démarrer l&apos;abonnement <ArrowRight className="h-4 w-4 ml-2" /></Button>
            </Link>
            <p className="text-xs text-center text-slate-500 mt-2">Paiement sécurisé via PayPal</p>
          </CardContent>
        </Card>
        <Card className="border-2 border-emerald-500 hover:border-emerald-600 transition-all shadow-xl shadow-emerald-100 relative" data-testid="pricing-annual">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">MEILLEURE OFFRE</div>
          <CardContent className="p-6 sm:p-7">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center"><Star className="h-5 w-5 text-emerald-600 fill-emerald-600" /></div>
              <span className="text-xs font-semibold text-emerald-700 uppercase">Abonnement annuel</span>
            </div>
            <p className="font-bold text-lg mb-1">Formule annuelle — 2 mois offerts</p>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl sm:text-5xl font-bold text-emerald-600">299€</span><span className="text-slate-500">/an</span>
              <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">≈ 24,92€/mois</span>
            </div>
            <p className="text-xs text-emerald-700 font-medium mb-5">Économisez l&apos;équivalent de 2 mois par rapport à 12 mensualités (359,88 €)</p>
            <PlanItems color="text-emerald-600" bold items={['Tout du mensuel, sur 12 mois', 'Patrithèque (9 guides inclus)', 'Mises à jour des taux incluses', 'Résiliable avant l\'échéance annuelle']} />
            <Link to="/achat?plan=annual" className="block" data-testid="pricing-cta-annual"
              onClick={() => { track('mon_ae_annual_click', { plan: 'annual', location: 'home' }); fbTrack('InitiateCheckout', { content_name: 'Abonnement Annuel', value: 299, currency: 'EUR' }); }}>
              <Button size="lg" className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-bold">S&apos;abonner à l&apos;année — 299€ <ArrowRight className="h-4 w-4 ml-2" /></Button>
            </Link>
            <p className="text-xs text-center text-slate-500 mt-2">Paiement sécurisé via PayPal</p>
          </CardContent>
        </Card>
      </div>
      <div className="text-center mt-8">
        <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-full text-xs sm:text-sm text-emerald-900" data-testid="pricing-guarantee">
          <Shield className="h-4 w-4 text-emerald-600" />
          <strong>Garantie 14 jours satisfait ou remboursé</strong> · sur les deux formules, voir <Link to="/cgv" className="underline">CGV</Link>
        </div>
      </div>
    </div>
  </section>
);

const Header = ({ user }) => {
  const firstName = user?.first_name || user?.email?.split('@')[0] || '';
  const initial = (firstName || 'U').charAt(0).toUpperCase();
  return (
    <header className="sticky top-0 z-40 glass border-b border-emerald-100/60" data-testid="home-header">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
        <Link to="/" className="font-bold text-base sm:text-xl font-['Manrope'] whitespace-nowrap" data-testid="header-logo">
          <span className="text-emerald-600">MON AE</span> <span className="text-xs sm:text-sm font-medium text-slate-500">par Capsule Patrimoine</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
          <button type="button" onClick={() => document.getElementById('tarifs')?.scrollIntoView({ behavior: 'smooth' })} className="text-xs sm:text-sm text-slate-600 hover:text-emerald-700 font-medium hidden sm:inline" data-testid="header-link-tarifs">Tarifs</button>
          {user ? (
            <Link to="/dashboard" className="flex items-center gap-2 group" data-testid="header-user-menu">
              <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold text-sm shadow-sm" data-testid="header-user-avatar">{initial}</div>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4 gap-1.5" data-testid="header-cta-dashboard">
                <LayoutDashboard className="h-3.5 w-3.5" /><span className="sm:hidden">Dashboard</span><span className="hidden sm:inline">Mon dashboard</span>
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login" className="text-xs sm:text-sm text-slate-700 hover:text-emerald-700 font-semibold whitespace-nowrap" data-testid="header-link-login">Connexion</Link>
              <Link to="/demo" data-testid="header-cta-essayer">
                <Button size="sm" variant="outline" className="border-emerald-600 text-emerald-700 text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4">
                  <span className="sm:hidden">Simulateur</span><span className="hidden sm:inline">Simulateur gratuit</span>
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-slate-900 text-slate-300 px-4 sm:px-6 py-10" data-testid="home-footer">
    <div className="max-w-6xl mx-auto">
      <div className="grid sm:grid-cols-3 gap-8 mb-8">
        <div>
          <p className="font-bold text-white text-lg font-['Manrope'] mb-2"><span className="text-emerald-400">MON AE</span> · Capsule Patrimoine</p>
          <p className="text-xs text-slate-400">Le tableau de bord des micro-entrepreneurs français. Par Valérie Girin.</p>
        </div>
        <div>
          <p className="text-xs font-bold text-white uppercase mb-3">Liens utiles</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/demo" className="hover:text-emerald-400" data-testid="footer-link-demo">Simulateur gratuit</Link></li>
            <li><Link to="/conformite-2026" className="hover:text-emerald-400" data-testid="footer-link-conformite">Réforme facturation 2026-2027</Link></li>
            <li><Link to="/login" className="hover:text-emerald-400" data-testid="footer-link-login">Se connecter</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold text-white uppercase mb-3">Légal & Contact</p>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2"><Mail className="h-3 w-3" /><a href="mailto:contact@capsulepatrimoine.com" className="hover:text-emerald-400">contact@capsulepatrimoine.com</a></li>
            <li className="text-xs text-slate-400">SIRET 484 280 771 00060</li>
            <li className="flex flex-wrap gap-x-2 gap-y-1 text-xs">
              <Link to="/mentions-legales" className="text-slate-400 hover:text-emerald-400" data-testid="footer-link-mentions">Mentions légales</Link><span className="text-slate-600">·</span>
              <Link to="/cgv" className="text-slate-400 hover:text-emerald-400" data-testid="footer-link-cgv">CGV</Link><span className="text-slate-600">·</span>
              <Link to="/politique-de-confidentialite" className="text-slate-400 hover:text-emerald-400" data-testid="footer-link-confidentialite">Confidentialité</Link><span className="text-slate-600">·</span>
              <Link to="/politique-cookies" className="text-slate-400 hover:text-emerald-400" data-testid="footer-link-cookies">Cookies</Link><span className="text-slate-600">·</span>
              <button type="button" onClick={() => window.dispatchEvent(new Event('cp-open-consent'))} className="text-slate-400 hover:text-emerald-400" data-testid="footer-manage-cookies">Gérer mes cookies</button>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800 pt-5 text-xs text-slate-500 text-center">© 2026 Capsule Patrimoine — Tous droits réservés.</div>
    </div>
  </footer>
);

const HomePage = () => {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-white">
      <Header user={user} />
      <HeroSection />
      <SoftwareSection />
      <ExampleSection />
      <PromisesSection />
      <SimulatorSection />
      <PricingSection />
      <EInvoicingSection />
      <FaqSection />
      <FinalCta />
      <Footer />
    </div>
  );
};

export default HomePage;
